import { Exceptions } from "../util/Translations"
import { Concept, IItem } from "../models"
import { IItemStatus } from "../models"
import { ConceptDTO, ItemDTO } from "./models/fields"
import { ODataGetResourceResponse } from "./models/api/data"
import { RequestErrorDataApi } from "../errors"
import { StatusDTO } from "./models/dto/concept"
import { dataAPI } from "./api"
import { formatString } from "../util/Strings"
import { parseStatusDto } from "./utils/itemParsing"

export const getCurrentItemStatus = (id: number): Promise<IItemStatus> =>
    dataAPI
        .getJSON<ODataGetResourceResponse<StatusDTO>>(`Items/${id}/Status`)
        .then((response) => parseStatusDto(response))

export const getItemAncestry = async (itemId: number): Promise<IItem[]> => {
    const parents = []
    let id = itemId
    let shouldContinueSearch = true
    // Escape hatch to make sure we don't fetch never ending hierarchies
    let index = 0
    while (shouldContinueSearch && index < 10) {
        const parent = await getItemParent(id)
        if (parent === null || !parent.id) {
            shouldContinueSearch = false
        } else {
            parents.push(parent)
            id = parent.id
        }
        index++
    }
    if (index === 10 && shouldContinueSearch) {
        console.warn(
            `Stopped fetching parents for item [${itemId}] as there seems to be too many.`,
        )
    }
    return parents.reverse()
}

export const getItem = async (itemId: number): Promise<IItem | null> => {
    if (itemId) {
        try {
            const itemDTO = await dataAPI.getJSON<ItemDTO>(`Items/${itemId}`)
            if (itemDTO) {
                return {
                    id: itemDTO.Id,
                    number: itemDTO.Number,
                    title: itemDTO.Label,
                }
            } else {
                return null
            }
        } catch (error) {
            if (error instanceof Response) {
                if (error.status === 404) {
                    return null
                }
                const errorMessage = formatString(Exceptions.Error, itemId)
                throw await RequestErrorDataApi.create(
                    errorMessage,
                    error.status,
                    error,
                )
            }
            throw error
        }
    } else {
        return null
    }
}

export const getItemParent = async (itemId: number): Promise<IItem | null> => {
    if (itemId) {
        try {
            const parent = await dataAPI.getJSON<ItemDTO>(
                `Items/${itemId}/Parent`,
            )
            if (parent) {
                return {
                    id: parent.Id,
                    number: parent.Number,
                    title: parent.Label,
                }
            } else {
                return null
            }
        } catch (error) {
            if (error instanceof Response) {
                if (error.status === 404) {
                    return null
                }
                const errorMessage = formatString(
                    Exceptions.AnErrorOccurredWhileFetchingParents,
                    itemId,
                )
                throw await RequestErrorDataApi.create(
                    errorMessage,
                    error.status,
                    error,
                )
            }
            throw error
        }
    } else {
        return null
    }
}

export const getItemConcept = async (
    itemId: number,
): Promise<Partial<Concept> | null> => {
    if (itemId) {
        try {
            const concept = await dataAPI.getJSON<ConceptDTO>(
                `Items/${itemId}/Concept`,
            )
            if (concept) {
                return {
                    conceptId: concept.Id,
                    conceptLabel: concept.Label,
                    conceptCode: concept.Code,
                    isTemplate: false, // Not provided by the API atm.
                    isActive: concept.IsActive,
                }
            } else {
                return null
            }
        } catch (error) {
            if (error instanceof Response) {
                if (error.status === 404) {
                    return null
                }
                const errorMessage = formatString(Exceptions.Error, itemId)
                throw await RequestErrorDataApi.create(
                    errorMessage,
                    error.status,
                    error,
                )
            }
            throw error
        }
    } else {
        return null
    }
}
